import { Alert, Box, Grid, Tab, Tabs, styled } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import * as React from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { DrawStage } from "@toorak/tc-common-fe-sdk";
import { DrawAmountDetails } from "./DrawAmountDetails";
import { DrawDetailsLoanEconomics } from "./DrawDetailsLoanEconomics";
import { DrawLoanInformation } from "./DrawLoanInformation";
import { bankDetailAlertSx, useDrawsStyles } from "../Draws.styles";
// import { DocsFormWrapper } from "../../DocumentsUpload/DocsFormWrapper";
import { isOrigUser, canViewWireVerification } from "../../../utils/AccessManagement";
import { drawLeftTabNames, getColorBasedOnStatus } from "../Constant";
import { BankDetailsWrapper } from "./BankDetailsWrapper";
// import { InternalNotesTabs } from "../InternalNotes/InternalNotesTabs";
import { ProvisionHistoryWrapper } from "../Provision/ProvisionHistoryWrapper";
import { HtmlWhiteTooltip } from "../../../utils/Tooltip";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

export const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const a11yProps = (index: number) => ({
  id: `simple-tab-${index}`,
  "aria-controls": `simple-tabpanel-${index}`
});

const StyledInfoIcon = styled(InfoOutlinedIcon)({
  width: 15,
  height: 15,
  marginLeft: 5,
  marginTop: 2,
  color: "#fff"
});

const compactChipStyle = {
  padding: "1px 7px 1px",
  fontSize: "10px",
  lineHeight: 1.1
} as const;

const WireVerificationStatusChip = ({
  status,
  stageDisplayClass
}: {
  status?: string;
  stageDisplayClass: string;
}) => {
  const isCompleted = status === "Completed";
  const label = isCompleted ? "Wire Verification Completed" : "Wire Verification Pending";
  const Icon = isCompleted ? CheckCircleIcon : WarningIcon;

  return (
    <span
      className={stageDisplayClass}
      data-testid="wire-verification-status"
      style={{
        backgroundColor: getColorBasedOnStatus(status ?? "Pending"),
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        ...compactChipStyle
      }}
    >
      <Icon
        style={{
          fontSize: 13,
          color: "#ffffff",
          fill: "#ffffff"
        }}
      />
      <span style={{ color: "#ffffff", fontSize: "10px", fontWeight: 500 }}>
        {label}
      </span>
    </span>
  );
};


const DocsFormWrapper = React.lazy(() =>
  import("../../DocumentsUpload/DocsFormWrapper").then((module) => ({
    default: module.DocsFormWrapper
  }))
);
const InternalNotesTabs = React.lazy(() =>
  import("../InternalNotes/InternalNotesTabs").then((module) => ({
    default: module.InternalNotesTabs
  }))
);

export const DrawTabs = ({
  selectedLoanId,
  drawObj,
  isAmounteditable,
  isSubmitClicked,
  showErrorMsg,
  isBulkDraw,
  isEditing,
  provisionHistory,
  setSnackBarMsg,
  setOpenSnackBar
}: any) => {
  const [value, setValue] = React.useState(0);
  const { drawId } = useParams<any>() as any;
  const isOrig = isOrigUser();
  const classes = useDrawsStyles();
  const selectedBankDetails = useSelector(
    (state: any) => state?.drawRequestStore?.selectedBankDetails
  );

  const handleChange = (event: any, newValue: number) => {
    setValue(newValue);
  };

  const internalsEnabled = () =>
    drawObj?.stage !== undefined && drawObj.stage.toUpperCase() !== "DRAFT";

  const noInternalTab = drawLeftTabNames.filter(
    (it) => it.label !== "Internal Discussions"
  );
  const tabNames = isOrig
    ? noInternalTab
    : drawObj?.stage === DrawStage.draft
      ? noInternalTab
      : drawLeftTabNames;
  const fieldWidth: 2 | 3 = drawObj?.stage === DrawStage.draft ? 2 : 3;
  const hasSelectedLoanId = String(selectedLoanId || "").trim() !== "";
  const hasBankDetailsAdded = Boolean(
    selectedBankDetails?.bankDetailId ||
    selectedBankDetails?.accountNumber ||
    selectedBankDetails?.routing ||
    selectedBankDetails?.bankName ||
    selectedBankDetails?.accountName
  );
  const canSeeWireVerification = canViewWireVerification(drawObj?.originatorName);
  const BankDetailsAlert = () => {
    if (drawObj?.stage === DrawStage.draft || !hasSelectedLoanId || !hasBankDetailsAdded) {
      return null;
    }
    if (!canSeeWireVerification) {
      return null;
    }

    const repeatList = drawObj?.repeatBankDetailsDrawList ?? [];

    return (
      <Alert severity="info" sx={bankDetailAlertSx} >
        <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          {isOrig ? (
            <span />
          ) : repeatList.length === 0 ? (
            "New Bank Account Details"
          ) : (
            <span>
              Bank account details same as Draw ID -{" "}
              <strong style={{ textDecoration: "underline" }}>
                {repeatList[0]?.drawId}
              </strong>
            </span>
          )}
          <WireVerificationStatusChip
            status={drawObj?.wireVerificationStatus}
            stageDisplayClass={classes.stageDisplay} />
        </Box>
      </Alert>
    );
  };
  return (
    <Box style={{ width: "100%", marginLeft: 20 }}>
      <Box style={{ borderBottom: 1, borderColor: "divider" }}>
        {
          <BankDetailsAlert/>}
        <Tabs
          value={value}
          aria-label="draw tabs"
          TabIndicatorProps={{
            style: {
              left: value === 0 ? 0 : value === 1 ? 110 : 220,
              width: value === 2 ? 170 : 110
            }
          }}
        >
          {tabNames.map((item: any, idx: number) => (
            <Tab
              key={idx}
              label={item.label}
              {...a11yProps(idx)}
              className={idx === value ? `tab-menu ${classes.tabSelected}` : "tab-menu"}
              onClick={(e) => {
                handleChange(e, idx);
              }}
            />
          ))}
        </Tabs>
      </Box>
      <TabPanel value={value} index={0}>
        <DrawLoanInformation
          isSubmitClicked={isSubmitClicked}
          showErrorMsg={showErrorMsg}
          isBulkDraw={isBulkDraw}
          fieldWidth={fieldWidth}
          setSnackBarMsg={setSnackBarMsg}
          setOpenSnackBar={setOpenSnackBar}
        />

        <DrawDetailsLoanEconomics drawObj={drawObj} fieldWidth={fieldWidth} />
        {!isOrig && (
          <ProvisionHistoryWrapper provisionHistory={provisionHistory} />
        )}
        <DrawAmountDetails
          drawObj={drawObj}
          isAmounteditable={isAmounteditable}
          selectedLoanId={selectedLoanId}
          isSubmitClicked={isSubmitClicked}
          showErrorMsg={showErrorMsg}
          fieldWidth={fieldWidth}
        />
        <BankDetailsWrapper
          selectedLoanId={selectedLoanId}
          isSubmitClicked={isSubmitClicked}
          drawObj={drawObj}
          isEditing={isEditing}
          fieldWidth={fieldWidth}
          setSnackBarMsg={setSnackBarMsg}
          setOpenSnackBar={setOpenSnackBar}
        />
      </TabPanel>
      <TabPanel value={value} index={1}>
        <DocsFormWrapper drawId={drawId} />
      </TabPanel>
      <TabPanel value={value} index={2}>
        <Grid
          item
          container
          xs={12}
          className={classes.itemContainer}
          data-testid="draw-docs-wrapper"
          style={{ padding: "0px 5px" }}
        >
          {internalsEnabled() && <InternalNotesTabs />}
        </Grid>
      </TabPanel>
    </Box>
  );
};
